// frontend/src/contexts/AlertContext.tsx
// TaisaCore Sprint 10: Alert Context for Bear and System Alerts

import React, { createContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { BearAlert, AlertContextValue } from '../types/dashboard';

export const AlertContext = createContext<AlertContextValue | null>(null);

interface AlertProviderProps {
  children: ReactNode;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<BearAlert[]>([]);

  // Add a new alert
  const addAlert = useCallback((alertData: Omit<BearAlert, 'id' | 'timestamp'>) => {
    const newAlert: BearAlert = {
      ...alertData,
      id: `alert-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setAlerts((prev) => {
      // Prevent duplicate alerts with same message
      if (prev.some((a) => a.shortMessage === newAlert.shortMessage)) {
        return prev;
      }
      return [newAlert, ...prev].slice(0, 20); // Keep max 20 alerts
    });
  }, []);

  // Dismiss an alert by ID
  const dismissAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  // Clear all alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  // Get critical alerts (sorted by timestamp)
  const criticalAlerts = useMemo(() => {
    return alerts
      .filter((alert) => alert.severity === 'critical')
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
  }, [alerts]);

  const value: AlertContextValue = {
    alerts,
    criticalAlerts,
    addAlert,
    dismissAlert,
    clearAlerts,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

// Custom hook for using alerts
export const useAlerts = (): AlertContextValue => {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

export default AlertContext;
